import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { useBrand } from '../context/BrandContext';
import { useState } from 'react';

export default function Contact() {
  const { content } = useBrand();
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '', honeypot: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send communication');
      }
      
      setStatus('success');
    } catch (error: any) {
      console.error('Contact form error:', error);
      setErrorMsg(error.message);
      setStatus('idle');
    }
  };

  return (
    <div className="pt-32 pb-24 bg-ows-white min-h-screen">
      <Helmet>
        <title>Contact Headquarters | OWS</title>
        <meta name="description" content="Contact Open Water Syndicate for corporate events, expedition clearance, or specialized gear." />
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-display font-light uppercase tracking-widest text-ows-dark mb-6">
            HEADQUARTERS
          </h1>
          <p className="text-lg md:text-xl text-ows-dark/60 max-w-2xl mx-auto font-light">
            Secure communications. Contact us for corporate syndicate memberships, private expeditions, and coiled equipment inquiries.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-5xl mx-auto">
          <div>
            <h3 className="text-[10px] font-bold tracking-[0.4em] uppercase text-ows-teal mb-8 font-mono">Operations Deck</h3>
            
            <div className="space-y-8 text-ows-dark mb-12">
              <div className="flex items-start gap-4">
                <MapPin className="h-5 w-5 text-ows-teal shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-sm uppercase tracking-widest mb-1">Base of Operations</h4>
                  <p className="italic text-black/60 text-sm">Toronto Outer Harbour, Leslie Street Spit</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="h-5 w-5 text-ows-teal shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-sm uppercase tracking-widest mb-1">Secure Channel</h4>
                  <p className="italic text-black/60 text-sm">contact@openwatersyndicate.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="h-5 w-5 text-ows-teal shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-sm uppercase tracking-widest mb-1">Direct Line</h4>
                  <p className="italic text-black/60 text-sm">(416) 477-1438</p>
                </div>
              </div>
            </div>
            
            <div className="bg-ows-grey p-8 rounded-xl border border-ows-teal/10">
              <h4 className="font-bold text-sm uppercase tracking-widest mb-4">Corporate Accounts</h4>
              <p className="text-sm text-black/60 leading-relaxed mb-6">
                Specialized corporate team building and multi-day expedition events require clearance from operations personnel. Response times are typically within 24 operational hours.
              </p>
            </div>
          </div>

          <div>
            {status === 'success' ? (
              <div className="bg-ows-teal text-white p-12 rounded-xl text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Send className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-widest mb-4">Transmission Sent</h3>
                <p className="text-sm opacity-80">Our operations team will review your message shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot field - hidden from users to catch bots */}
                <div style={{ opacity: 0, position: 'absolute', top: 0, left: 0, height: 0, width: 0, zIndex: -1 }}>
                  <label htmlFor="company-website">Company Website</label>
                  <input type="text" id="company-website" name="company-website" autoComplete="off" tabIndex={-1} value={formData.honeypot} onChange={(e) => setFormData({...formData, honeypot: e.target.value})} />
                </div>
                
                {errorMsg && (
                  <div className="bg-red-500/10 border border-red-500/20 text-red-600 px-4 py-3 rounded-lg text-[10px] uppercase tracking-widest font-bold">
                    [ERROR] {errorMsg}
                  </div>
                )}
                
                <div>
                  <label className="block text-[10px] font-bold tracking-[0.3em] uppercase text-ows-dark/60 mb-2">Operator Name</label>
                  <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full bg-ows-grey border border-ows-teal/10 rounded-lg px-4 py-3 focus:border-ows-teal focus:ring-1 focus:ring-ows-teal outline-none transition-colors" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold tracking-[0.3em] uppercase text-ows-dark/60 mb-2">Comms Channel (Email)</label>
                  <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full bg-ows-grey border border-ows-teal/10 rounded-lg px-4 py-3 focus:border-ows-teal focus:ring-1 focus:ring-ows-teal outline-none transition-colors" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold tracking-[0.3em] uppercase text-ows-dark/60 mb-2">Subject</label>
                  <select required value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} className="w-full bg-ows-grey border border-ows-teal/10 rounded-lg px-4 py-3 focus:border-ows-teal focus:ring-1 focus:ring-ows-teal outline-none transition-colors">
                    <option value="">Select Topic</option>
                    <option value="corporate">CORPORATE Team Building</option>
                    <option value="training">Private Training</option>
                    <option value="gear">OWS EXPEDITION CX2 Vessel Pre-order</option>
                    <option value="other">General Inquiry</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold tracking-[0.3em] uppercase text-ows-dark/60 mb-2">Message</label>
                  <textarea required rows={5} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} className="w-full bg-ows-grey border border-ows-teal/10 rounded-lg px-4 py-3 focus:border-ows-teal focus:ring-1 focus:ring-ows-teal outline-none transition-colors resize-none"></textarea>
                </div>
                <button type="submit" disabled={status === 'submitting'} className="w-full bg-ows-dark text-white px-8 py-4 rounded-xl font-bold uppercase text-xs tracking-widest hover:bg-ows-teal hover:text-ows-dark transition-colors flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed">
                  {status === 'submitting' ? (
                    <span className="flex items-center gap-2"><div className="w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin"></div> Transmitting...</span>
                  ) : (
                    <><Send className="h-4 w-4" /> Transmit Message</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
